import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@et/schema';
import { Icon } from '@et/ui';
import { fetchSearch, queryKeys, type ContentRev, type SearchHit } from '../lib/api.js';
import { localePath } from '../lib/locale.js';

/**
 * A pesquisa na barra, para chegar a uma peca pelo nome.
 *
 * Ate aqui a unica forma de encontrar a Pedra de Pilatos era filtrar e
 * percorrer. Num acervo com sessenta evidencias, quarenta passagens e vinte
 * claims, isso e um custo por visita. Duas letras chegam para comecar; o
 * pedido so sai depois de a pessoa parar de escrever, para nao fazer uma
 * chamada por tecla.
 *
 * Escolher um resultado navega — a caixa nao guarda estado nenhum alem do
 * texto, por isso fechar e reabrir e sempre limpo.
 */
export function SearchBox({ locale, rev }: { readonly locale: Locale; readonly rev: ContentRev | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const listId = useId();
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = window.setTimeout(() => setQ(text.trim()), 200);
    return () => window.clearTimeout(handle);
  }, [text]);

  const enabled = rev !== null && q.length >= 2;
  const hits = useQuery({
    queryKey: rev === null ? ['search', q] : queryKeys.search(rev, locale, q),
    queryFn: () => fetchSearch(rev!.version, locale, q),
    enabled,
    staleTime: 60_000,
  });
  const items = enabled ? (hits.data?.items ?? []) : [];

  useEffect(() => { setActive(0); }, [q]);

  // Carregar fora fecha a lista; carregar dentro nao.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (root.current !== null && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const hrefOf = (h: SearchHit): string => {
    switch (h.type) {
      case 'evidence': return localePath(locale, 'evidence', h.id);
      case 'claim': return localePath(locale, 'claim', h.id);
      case 'passage': return `${localePath(locale, 'text')}?passage=${encodeURIComponent(h.id)}`;
      case 'objection': return `${localePath(locale, 'objections')}#${encodeURIComponent(h.id)}`;
      case 'topic': return localePath(locale, 'topic', h.id);
      // Uma fonte nao tem pagina propria: abre onde ela e citada, que e a
      // ficha da evidencia. Ate la, a pesquisa leva ao indice.
      case 'source': return localePath(locale);
    }
  };
  const go = (h: SearchHit) => {
    setOpen(false);
    setText('');
    void navigate(hrefOf(h));
  };

  const showList = open && q.length >= 2;

  return (
    <div className="et-search" ref={root}>
      <Icon name="search" size={15} className="et-search__icon" />
      <input
        type="search"
        className="et-search__input"
        role="combobox"
        aria-label={t('nav.search')}
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        placeholder={t('search.placeholder')}
        value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); return; }
          if (!showList || items.length === 0) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          if (e.key === 'Enter') { e.preventDefault(); const h = items[active]; if (h !== undefined) go(h); }
        }}
      />
      {showList && (
        <div className="et-search__panel" id={listId} role="listbox">
          {items.length === 0 && hits.isFetched && (
            <p className="et-search__empty">{t('search.noResults', { q })}</p>
          )}
          {items.map((h, i) => (
            <button
              key={`${h.type}:${h.id}`}
              type="button"
              role="option"
              aria-selected={i === active}
              className="et-search__hit"
              data-type={h.type}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(h)}
            >
              <span className="et-search__type">{t(`search.type.${h.type}`)}</span>
              <span className="et-search__body">
                <span className="et-search__title">{h.title}</span>
                {/* O excerto so aparece quando casou no corpo: sem ele, uma
                    pesquisa por prosa devolvia titulos sem a palavra procurada
                    e parecia um erro. */}
                {h.snippet !== null && <span className="et-search__snippet">{h.snippet}</span>}
              </span>
            </button>
          ))}
          {items.length === 0 && !hits.isFetched && (
            <p className="et-search__empty">{t('search.hint')}</p>
          )}
        </div>
      )}
    </div>
  );
}
